import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './AnalysisList.module.scss';
import { ANALYSIS_TYPE_NAMES } from 'config/labels';
import { getAllowedAnalysisTypes } from 'config/browserConfig';
import { useAnalysisPage } from '../useAnalysisPage';
import {
  AnalysisType,
  getRefreshAnalysisType,
} from 'config/analysisConfig';
import { Series } from '@models/Series';
import { ReactComponent as Right } from 'img/chevron-right.svg';
import { ReactComponent as Down } from 'img/chevron-down.svg';
import { usePanelContainer } from '../rendering/Panels/usePanelContainer';
import { useBrowserPage } from '@features/browser/useBrowserPage';
import { useEjectionFractionModal } from '../analysisModal/EjectionFractionModal/EjectionFractionModal';
import {
  addRequestPool,
  deactivateExclusiveTools,
  getPreViewVolumeFromSeriesId,
  getVolumeFromSeriesId,
} from '../rendering/Panels/Renderers/CornerstoneManager';

import {
  useBufferPool,
} from '../rendering/Panels/Renderers/vtkManagers';
import { useConfig } from 'config/useConfig';
import { configStore } from 'config/configStore';
import {
  AnalysisBaseStatus,
  getAnalysisStatusByName,
  getDefaultItem,
  isSegmentationSucceeded,
  SeriesAnalysisStatusMap,
  statusToBool,
} from 'utils/AnalysisBaseUtilities/AnalysisBaseUtilities';
import useModalActions from '@hooks/common/useModalAction';
import { useAuthPage } from '@features/auth/useAuthPage';
import { useAnalysisBaseUtilities } from 'utils/AnalysisBaseUtilities/useAnalysisBaseUtilities';
import { SavedInfo } from '@models/Parameter';
import {
  isAnalysisTypeAvailableByServerState,
  useServerFeatureState,
} from 'config/serverFeatureState';

export interface AnalysisListProps {
  seriesList: Series[];
  // imageList: Image[][];
  allowedAnalysisIds: string[];
  analysisBaseInfoBySeries:SeriesAnalysisStatusMap
  // onSelect: (selected: { seriesId: string; analysisTypeId?: string }) => void;
}

function AnalysisList({
  seriesList,
  allowedAnalysisIds,
  analysisBaseInfoBySeries,
}: AnalysisListProps): React.JSX.Element {

  const { selectedSeriesId, selectedSeries } = useBrowserPage();
  const {
    currentVOI,
    updateAnalysisDefault,
    selectedAnalysis,
    isLayoutStateLoading,
    updateSelectAnalysis,
    handleLayoutStateLoading,
    updateWindowLevelWidth,
    updateVOIInvert,
  } = useAnalysisPage();
  const {
    panelStates,
    updatePanelState,
    analysisType: currentAnalysisType,
  } = usePanelContainer();
  const EjectionFractionModalOpen = useEjectionFractionModal();

  const { config, setSeriesID } = useConfig();
  const {bufferPool,clearPool} = useBufferPool();
  const { waitingAction, closeLoadingModal, successAction } = useModalActions();
  const { accessToken } = useAuthPage();
  const serverFeatureState = useServerFeatureState(accessToken);
  // const [analysisBaseInfoBySeries, setAnalysisBaseInfoBySeries] = useState<
  //   Record<
  //     string,
  //     { analysisStatus?: string | null; analysisType?: { name?: string | null } | null }[]
  //   >
  // >({});
  // const analysisBaseInfoBySeriesRef = useRef(analysisBaseInfoBySeries);

  const [isVolumeFromSeries, setIsVolumeFromSeries] = useState(false);

  useEffect(() => {
    if (!selectedSeriesId) {
      setIsVolumeFromSeries(false);
      return;
    }

    const isNotHighQuality =
      configStore.get().generalSetting.renderingQuality !== 'high';

    const checkVolumeFromSeries = () => {
      const exists = !!getVolumeFromSeriesId(
        selectedSeriesId,
        isNotHighQuality,
      );
      console.log(
        '[AnalysisList] volume cache check',
        `seriesId=${selectedSeriesId}`,
        `exists=${exists}`,
      );
      setIsVolumeFromSeries((prev) => (prev === exists ? prev : exists));
      return exists;
    };

    const isReady = checkVolumeFromSeries();
    if (isReady) {
      console.log(
        '[AnalysisList] volume cache already ready, skip polling',
        `seriesId=${selectedSeriesId}`,
      );
      return;
    }

    console.log(
      '[AnalysisList] start volume cache polling',
      `seriesId=${selectedSeriesId}`,
      'interval=500ms',
    );
    let intervalId: number | null = window.setInterval(() => {
      const exists = checkVolumeFromSeries();
      if (exists && intervalId !== null) {
        window.clearInterval(intervalId);
        console.log(
          '[AnalysisList] stop volume cache polling',
          `seriesId=${selectedSeriesId}`,
          'reason=cache-ready',
        );
        intervalId = null;
      }
    }, 500);

    return () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId);
        console.log(
          '[AnalysisList] stop volume cache polling',
          `seriesId=${selectedSeriesId}`,
          'reason=cleanup',
        );
      }
    };
  }, [selectedSeriesId]);

  const isSelectedSeriesLoading =
    !!selectedSeriesId &&
    (isLayoutStateLoading ||
      !bufferPool[selectedSeriesId] ||
      !bufferPool[selectedSeriesId].parameter.loaded);

  const block =
    isLayoutStateLoading ||
    !selectedSeriesId ||
    !bufferPool[selectedSeriesId] ||
    !bufferPool[selectedSeriesId].parameter.loaded;
  const { getSeriesAnalysisStatus, runRefreshData } =
    useAnalysisBaseUtilities();
  const refreshStateRef = useRef<{
    inFlight: boolean;
    prevAnalysisType?: AnalysisType;
  }>({ inFlight: false });

  const runRealtimeRefresh = useCallback(
    (analysisType: AnalysisType, isLoading: boolean, isUpdata?: boolean) => {
      if (refreshStateRef.current.inFlight) return;
      refreshStateRef.current.inFlight = true;
      console.log('????, ', analysisType, isLoading, isUpdata);
      return runRefreshData(analysisType, isLoading, isUpdata)
        .then(() => {
          refreshStateRef.current.inFlight = false;
        })
        .catch((error) => {
          refreshStateRef.current.inFlight = false;
          throw error;
        });
    },
    [runRefreshData],
  );



  useEffect(() => {
    const state = refreshStateRef.current;
    console.log(
      'currentAnalysisType',
      currentAnalysisType,
      state.prevAnalysisType,
    );

    //얘는 실시간 갱신
    const prevAnalysisType = state.prevAnalysisType;
    if (prevAnalysisType !== currentAnalysisType) {
      state.prevAnalysisType = currentAnalysisType ?? null;
      return;
    }
    if (!getRefreshAnalysisType(currentAnalysisType)) {
      return;
    }

    const { segSucceeded } = getSeriesAnalysisStatus(
      selectedSeriesId!,
      analysisBaseInfoBySeries,
    );

    if (segSucceeded) {
      console.log('segSucceeded changed:', state);
      runRealtimeRefresh(currentAnalysisType, true, true);
    }

    state.prevAnalysisType = currentAnalysisType ?? null;
  }, [currentAnalysisType, analysisBaseInfoBySeries]);

  const getFfrWarningMessage = useCallback((seriesId?: string) => {
    if (!seriesId) return '';
    const coronary = (bufferPool[seriesId]?.parameter?.data as
      | { SavedInfo?: SavedInfo }
      | undefined)?.SavedInfo?.Coronary;
    return (coronary?.warningMessage_legacy ?? '').trim();
  }, [bufferPool]);

  const showFfrWarningMessage = useCallback((seriesId?: string) => {
    const warningMessage = getFfrWarningMessage(seriesId);
    if (!warningMessage || warningMessage.toLowerCase() === 'success') return;

    void successAction(warningMessage);
  }, [getFfrWarningMessage, successAction]);

  const handleAnalysisTypeClick = async (
    series: Series,
    analysisType?: AnalysisType,
  ) => {
    setSeriesID(series.id);

    if (analysisType !== 'FFR') {
      configStore.setData({ ffr_selected_centerline_id: null });
    }

    let windowLevel = currentVOI.windowLevel;
    let windowWidth = currentVOI.windowWidth;
    let newVOI = currentVOI;
    // 프리뷰는 이미 다 받았어.analysisPage에서
    if (series.id !==selectedSeriesId  ||  analysisType !== 'preview') {
      if(analysisType==='preview'){
         windowLevel = series.imageTag.windowCenter ?? windowLevel;
         windowWidth = series.imageTag.windowWidth ?? windowWidth;
         newVOI = {
            windowLevel,
            windowWidth,
          };
         updateWindowLevelWidth([newVOI.windowLevel, newVOI.windowWidth]);
         updateAnalysisDefault({
            [analysisType]:  newVOI ,
        });
      //  resetSomeSetting(); // 그녀석인가.
      }
      handleLayoutStateLoading(true);
    }

    const res = getSeriesAnalysisStatus(series.id, analysisBaseInfoBySeries);
    if (analysisType && analysisType !== 'empty') {
      const shouldRefresh =
        getRefreshAnalysisType(analysisType) && (res.segSucceeded);
      if (shouldRefresh) {
        await runRealtimeRefresh(analysisType,false);
      }
    }
    deactivateExclusiveTools({});
    updateSelectAnalysis(series, analysisType);
    //updateSwitchConfigs({ switchSynchronizingPanel: true });

     if (analysisType && analysisType !== 'empty') {
       const params = configStore.get().analysisParams[analysisType];
       console.log('currentVOI: ', currentVOI, params);

      if (params?.useDefaultWindow) {
        newVOI = {
          windowLevel: params.defaultWindowLevel,
          windowWidth: params.defaultWindowWidth,
          invert: params.isInverted ?? false,
        };

        updateWindowLevelWidth([newVOI.windowLevel, newVOI.windowWidth]);
        updateVOIInvert(!!newVOI.invert);
        updateAnalysisDefault({
            [analysisType]:  newVOI ,
        });
      }
    }
  };

  const seriesTitles = useMemo(() => {
    return seriesList.map((series, index) => {
      const phase = series.imageTag.phase;
      const number = series.seriesNumber ?? '';
      const desc = series.seriesDescription
        ? series.seriesDescription.length > 32
          ? series.seriesDescription.substring(0, 32) + '...'
          : series.seriesDescription
        : '';
      const count = series.imageTag?.depth ?? 0;
      return `${number}, ${desc}, ${count} images`;
      // return phase !== null && phase !== undefined
      //   ? `${phase}%, ${number}, ${desc}, ${count} images`
      //   : `${number}, ${desc}, ${count} images`;
    });
  }, [seriesList]);

  const allowedAnalysisTypesMap = useMemo(() => {
    const analysisParams = config.analysisParams;
    const analysisParamOrder = Object.keys(analysisParams);
    const orderIndex = new Map(
      analysisParamOrder.map((analysisType, index) => [analysisType, index]),
    );
    const map = new Map<string, AnalysisType[]>();

    for (const series of seriesList) {
      const allowed = getAllowedAnalysisTypes(
        series.modality,
        allowedAnalysisIds,
      )
        .filter((type) =>
          type === 'preview'
            ? true
            : analysisParams[type]?.enabled !== false &&
              isAnalysisTypeAvailableByServerState(type, serverFeatureState),
        )
        .sort(
          (a, b) =>
            (orderIndex.get(a) ?? Number.MAX_SAFE_INTEGER) -
            (orderIndex.get(b) ?? Number.MAX_SAFE_INTEGER),
        );
      map.set(series.id, allowed);
    }
    console.log(map)
    return map;
  }, [seriesList, allowedAnalysisIds, config.analysisParams, serverFeatureState]);

  // const statusToBool = (status: unknown): boolean | null => {
  //   if (status === 'Succeeded') return true;
  //   if (status === 'Failed') return false;
  //   return null;
  // };

  // const getAnalysisStatusByName = (
  //   items: { analysisStatus?: string | null; analysisType?: { name?: string | null } | null }[],
  // ) => {
  //   return new Map(
  //     items.map((item) => [
  //       item?.analysisType?.name?.toLowerCase() ?? '',
  //       statusToBool(item?.analysisStatus),
  //     ]),
  //   );
  // };

  // const isSegmentationSucceeded = (
  //   items: { analysisStatus?: string | null; analysisType?: { name?: string | null } | null }[],
  // ) => {
  //   const statusByName = getAnalysisStatusByName(items);
  //   return statusByName.get('segmentation') === true;
  // };

  const getStatusClass = (ok: boolean | null | undefined) => {
    if (ok === true) return 'ok';
    if (ok === false) return 'bad';
    return 'pending';
  };

  const getCombinedStatus = (
    statusByName: Map<string, boolean | null>,
    names: string[],
  ) => {
    const statuses = names.map((name) =>
      statusByName.get(name.toLowerCase()),
    );
    if (statuses.some((status) => status === false)) return 'bad';
    if (statuses.every((status) => status === true)) return 'ok';
    return 'pending';
  };

  const MODIFICATION_STATUS_NAMES = ['modify-regulator', 'modify-lumen'];

  const getCoronaryDependentStatus = (
    statusByName: Map<string, boolean | null>,
  ) => {
    const modificationStatuses = MODIFICATION_STATUS_NAMES.filter((name) =>
      statusByName.has(name),
    );

    if (modificationStatuses.length > 0) {
      return getCombinedStatus(statusByName, modificationStatuses);
    }

    return getStatusClass(statusByName.get('coronary'));
  };

  const getSegmentationStatusWithLicense = (
    statusByName: Map<string, boolean | null>,
  ) => {
    const hasSegmentation = statusByName.has('segmentation');
    const hasCoronary = statusByName.has('coronary');
    if (hasSegmentation && hasCoronary) {
      return getCombinedStatus(statusByName, ['segmentation', 'coronary']);
    }
    if (hasSegmentation) {
      return statusByName.get('segmentation') === true ? 'ok' : 'bad';
    }
    if (hasCoronary) {
      return statusByName.get('coronary') === true ? 'ok' : 'bad';
    }
    return 'pending';
  };


  const getAnalysisTypeStatus = (
    type: AnalysisType,
    statusByName: Map<string, boolean | null>,
    isSeriesSegmented: boolean,
  ) => {
    switch (type) {
      case 'MPR':
        return 'ok';
      case 'plaque':
      case 'fai':
      case 'stenosis':
      case 'isr':
      case 'calciumScore':
        return getStatusClass(statusByName.get('coronary'));
        //return getCoronaryDependentStatus(statusByName);
      case 'chamber':
        return getStatusClass(statusByName.get('segmentation'));
      case 'aorticValveCalcium':
        return getCombinedStatus(statusByName, ['aorta', 'aortacenterline']);
      case 'tavi':
        return getStatusClass(statusByName.get('autotavi'));
        // return 'ok';
      case 'myocardial':
        return getCombinedStatus(statusByName, ['segmentation', 'coronary']);
      case 'wallthickness':
        return getCombinedStatus(statusByName, ['segmentation', 'lawt']);
      case 'segmentation':
        return getSegmentationStatusWithLicense(statusByName);
      case 'ejectionFraction':
        return isSeriesSegmented && (Object.values(segmentationStatusBySeries).filter(Boolean).length >= 2) ? 'ok' : 'pending';
      case 'FFR':
        return getStatusClass(statusByName.get('ct-ffr'));
      default:
        return getStatusClass(statusByName.get(type.toLowerCase()));
    }
  };

  const getSeriesStatusClass = useCallback((items: AnalysisBaseStatus[]) => {
    return getStatusClass(statusToBool(getDefaultItem(items)?.status));
  },[]);


  const segmentationStatusBySeries = useMemo(() => {
    console.log(seriesList,analysisBaseInfoBySeries);

    return Object.fromEntries(
      seriesList.map((series) => {
        const items = analysisBaseInfoBySeries[series.id] ?? [];
        return [series.id, isSegmentationSucceeded(items)];
      }),
    );
  }, [analysisBaseInfoBySeries, seriesList]);
  // const shouldContinuePolling = (
  //   items: { analysisStatus?: string | null; analysisType?: { name?: string | null } | null }[],
  // ) => {
  //   const defaultItem = items.find(
  //     (item) => item?.analysisType?.name?.toLowerCase() === 'default',
  //   );
  //   const status = defaultItem?.analysisStatus;
  //   if (status === 'Succeeded' || status === 'Failed') return false;
  //   return true;
  // };


  return (
    <div className={styles.listColumn}>
      <div className={styles.seriesList}>
        {seriesList.map((series, index) => {
          // const seriesKey = getSeriesKey(series);
          // 각 Series의 modality와 allowedAnalysisIds를 기반으로 허용된 분석 유형 도출
          const isSelectedSeries = series.id===selectedAnalysis?.seriesId;
          const seriesBaseInfo = analysisBaseInfoBySeries[series.id] ?? [];
          const seriesStatusClass = getSeriesStatusClass(seriesBaseInfo);
          const analysisStatusByName = getAnalysisStatusByName(seriesBaseInfo);
          const isSeriesSegmented = segmentationStatusBySeries[series.id] === true;
          const allowedAnalysisTypes = allowedAnalysisTypesMap.get(series.id) ?? [];
          //tavi  일단 제외 아직 미완성
          const excluded =
            seriesList.length === 1
              ? ['ejectionFraction']
              : [];

          const newAnalysisTypes = allowedAnalysisTypes.filter(
            (i) => !excluded.includes(i),
          );
          return (
            <div key={`${series.id}-${index}`}>
              <div
                id={series.id}
                className={`${styles.seriesHeader} ${isSelectedSeries ? styles.selected : ''} ${isSelectedSeriesLoading ? styles.disable : ''}`}
                onClick={() => {
                  if (isSelectedSeriesLoading) return;
                  if(series.id === selectedSeriesId && selectedAnalysis?.analysisType === 'preview') return;
                  if(series.id !== selectedSeriesId) clearPool(series.id);
                  if (!getPreViewVolumeFromSeriesId(series.id)) {
                  //PreView용 캐쉬가 안만들어지면 다시 한번 다운로드 시도
                    waitingAction({ type: 'download', message: 'Loading data' });
                    addRequestPool({
                      accessToken,
                      series,
                      volumeId: `PREVIEW_${series.id}`,
                      filepath: `${series.id}_preview`,
                      priority: 0,
                      postFunction: (_fail?: boolean) => {
                        closeLoadingModal();
                        if (_fail) return; //?ㅽ뙣硫?鍮좎씠.
                        handleAnalysisTypeClick(series,'preview');
                      },
                    });
                  }else{
                    handleAnalysisTypeClick(series,'preview');
                  }
                }}
              >
                <button
                  className={styles.toggleButton}
                  aria-label={isSelectedSeries ? 'Expand' : 'Collapse'}
                >
                  {isSelectedSeries ? <Down /> : <Right />}
                </button>

                <span className={`${styles['status-bar']} ${styles[seriesStatusClass]}`} />
                <div className={styles.studyDetailsContainer}>
                  <span className={styles.studyDetailsText}>
                    {seriesTitles[index]}
                  </span>
                </div>
              </div>

              {isSelectedSeries && (
                <>
                  {!!newAnalysisTypes.length &&
                    newAnalysisTypes.map((type, index) => {
                      const analysisTypeStatusClass =
                        getAnalysisTypeStatus(type, analysisStatusByName, isSeriesSegmented);
                      const canSelectFfr =
                        type === 'FFR' &&
                        (analysisStatusByName.get('ct-ffr') === true ||
                          analysisStatusByName.get('ct-ffr') === false);
                      const canSelectAnalysisType =
                        type === 'MPR' ||
                        canSelectFfr ||
                        (type === 'segmentation'
                          ? analysisStatusByName.get('segmentation') === true ||
                            analysisStatusByName.get('coronary') === true
                          : analysisTypeStatusClass === 'ok');

                      const isVolume = ['MPR','empty'].includes(type) ? true : isVolumeFromSeries;
                      const disable = block || !canSelectAnalysisType || !isVolume;
                      return (
                        <span
                          key={type}
                          className={`${styles.tableItem}
                          ${index % 2 === 1 ? styles.oddRow : styles.evenRow}
                          ${selectedAnalysis?.analysisType === type ? styles.selected : ''}
                          ${disable ? styles.disable : ''}
                          ${type === 'FFR' && config.shouldRunFFR ? styles.hasPreviousBadge : ''}`}
                          onClick={() => {
                            if (!isVolume) {
                              console.log('아직없어요');
                              return;
                            }

                            const canClickAnalysis =
                              !(block && type !== 'MPR') && canSelectAnalysisType;
                            if (!canClickAnalysis) return;

                            if (type === 'FFR') {
                              showFfrWarningMessage(series.id);
                            }

                            if (
                              !isSelectedSeries ||
                              selectedAnalysis?.analysisType !== type
                            ) {
                              if (type === 'ejectionFraction') {
                                EjectionFractionModalOpen(
                                  selectedSeries!,
                                  type === 'ejectionFraction',
                                );
                              } else {
                                handleAnalysisTypeClick(series, type);
                              }
                            }
                          }}
                        >
                          <span
                            className={`${styles['status-bar']} ${styles[analysisTypeStatusClass]}`}
                          />
                          <span
                            className={styles.tableItemText}
                            onMouseEnter={(event) => {
                              const el = event.currentTarget;
                              if (el.scrollWidth > el.clientWidth) {
                                el.title = el.textContent ?? '';
                              } else {
                                el.removeAttribute('title');
                              }
                            }}
                          >
                            {ANALYSIS_TYPE_NAMES[series.modality][type]}
                          </span>
                          {type === 'FFR' && config.shouldRunFFR && (
                            <span className={styles.previousBadge}>
                              {' '}
                              Legacy FFR
                            </span>
                          )}
                        </span>
                      );
                    })}

                </>
              )}
            </div>
            );
        })}
      </div>
    </div>
    )
}

export default AnalysisList;
